import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { buildEffectiveFields, normalizeCustomField, FIELDS } from "@/lib/fields";

const FieldConfigContext = createContext(null);

export const useFieldConfig = () => useContext(FieldConfigContext) || {
  hidden: [], custom: [], fields: FIELDS, customFields: [], loading: false, refresh: () => {}, save: async () => {},
};

export function FieldConfigProvider({ children }) {
  const { user } = useAuth();
  const [hidden, setHidden] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Only fetch once a real user is authenticated; fetching while unauthenticated
    // triggers a 401 that the api.js interceptor turns into a hard /login redirect,
    // which aborts the Google OAuth session exchange in AuthCallback.
    if (!user) return;
    try {
      const cfg = await api.getFieldConfig();
      setHidden(cfg.hidden || []);
      setCustom(cfg.custom || []);
    } catch (e) { console.warn("field-config load failed (not authed / no config yet):", e); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = useCallback(async (next) => {
    const cfg = await api.saveFieldConfig(next);
    setHidden(cfg.hidden || []);
    setCustom(cfg.custom || []);
    return cfg;
  }, []);

  const fields = buildEffectiveFields(hidden, custom);
  const customFields = (custom || []).map(normalizeCustomField);

  return (
    <FieldConfigContext.Provider value={{ hidden, custom, fields, customFields, loading, refresh, save }}>
      {children}
    </FieldConfigContext.Provider>
  );
}
